import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import { Button, Modal } from 'ncoded-component-library';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import { Field, Form } from 'react-final-form';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import InputField from 'components/InputField';
import validations from 'validations';
import { BossModeSkipPayment } from 'models/Listing';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import SendingInvoiceToClient from '../SendingInvoiceToClient';
import SendingInvoiceToEd from '../SendingInvoiceToEd';
import DatepickerField from 'components/DatepickerField';
import EmailField from 'components/EmailField';

import './BmSkipPaymentModal.styles.scss';

type BmSkipPaymentModalProps = {
  className?: string;
  onSuccess: (data?: BossModeSkipPayment, popupContent?: string) => void;
};

const BmSkipPaymentModal: React.ForwardRefRenderFunction<
  ModalRef,
  BmSkipPaymentModalProps
> = (props, ref) => {
  const { className, onSuccess } = props;

  const classes = classNames('bm-skip-payment-modal', className);

  const [modal, modalRef] = useCallbackRef<ModalRef>();
  const [sendToEd, sendToEdRef] = useCallbackRef<ModalRef>();
  const [sendToClient, sendToClientRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const { isUltraAdmin } = useContext(CurrentUserContext);

  const { t } = useTranslation();

  const commonValidations = useValidations();

  const onSubmit = useCallback(
    ({
      clientEmailForGeneratingInvoice,
      transferListingToClient,
      reasonForNotGeneratingInvoice,
      ...rest
    }: BossModeSkipPayment) => {
      const data = {
        ...rest,
        reasonForNotGeneratingInvoice,
        ...(clientEmailForGeneratingInvoice
          ? { clientEmailForGeneratingInvoice, transferListingToClient }
          : {}),
      };

      onSuccess(
        data,
        reasonForNotGeneratingInvoice
          ? 'invoiceSentToEd'
          : 'invoiceConnectedWithXero',
      );
      modal.close();
    },
    [modal, onSuccess],
  );

  const skipPayment = useCallback(() => {
    onSuccess();
    modal.close();
  }, [modal, onSuccess]);

  return (
    <Modal ref={modalRef} className={classes} title={t('skippingPayment')}>
      <Form onSubmit={onSubmit}>
        {({ values, valid, form: { change, submit }, handleSubmit }) => {
          const { clientEmailForGeneratingInvoice } = values;

          return (
            <form onSubmit={handleSubmit}>
              <p>{t('skippingPaymentDescription')}</p>

              <div className="row">
                <Field
                  name="clientFirstNameForGeneratingInvoice"
                  component={InputField}
                  label={t('ownersFirstName')}
                  required
                  validate={commonValidations.name}
                />

                <Field
                  name="clientLastNameForGeneratingInvoice"
                  component={InputField}
                  label={t('ownersLastName')}
                  required
                  validate={commonValidations.lastName}
                />
              </div>

              <EmailField
                name="clientEmailForGeneratingInvoice"
                label={t('ownersEmail')}
                customOnBlur={(e) => {
                  if (!e.target.value)
                    change('transferListingToClient', undefined);
                }}
              />

              <Field
                name="clientCompanyForGeneratingInvoice"
                component={InputField}
                label={t('company')}
                required
                validate={validations.required()}
              />

              <Field
                name="paymentDueDate"
                component={DatepickerField}
                label={t('paymentDueDate')}
                required
                onlyFuture
                validate={validations.composeValidators(
                  validations.required(),
                  validations.onlyFuture(t('onlyFuture')),
                )}
              />

              <Field
                name="paymentDueDateDescription"
                component={InputField}
                label={t('paymentDueDateDescription')}
              />

              <Field
                name="transferListingToClient"
                component={CheckboxField}
                disabled={!clientEmailForGeneratingInvoice}
                type="checkbox"
                label={t('transferListingToClient')}
              />

              <div className="actions">
                {isUltraAdmin ? (
                  <Button variant="outline" type="button" onClick={skipPayment}>
                    {t('submitWithoutClientData')}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    type="button"
                    disabled={!valid}
                    onClick={() => sendToEd?.open()}
                  >
                    {t('sendToEd')}
                  </Button>
                )}
                <Button
                  type="button"
                  disabled={!valid}
                  onClick={() => {
                    if (!clientEmailForGeneratingInvoice) sendToClient?.open();
                    else submit();
                  }}
                >
                  {t('sendToClient')}
                </Button>
              </div>

              <SendingInvoiceToEd
                ref={sendToEdRef}
                onSuccess={({ reasonForNotGeneratingInvoice }) =>
                  onSubmit({ ...values, reasonForNotGeneratingInvoice })
                }
              />
              <SendingInvoiceToClient
                ref={sendToClientRef}
                onSuccess={({ clientEmailForGeneratingInvoice }) =>
                  onSubmit({ ...values, clientEmailForGeneratingInvoice })
                }
              />
            </form>
          );
        }}
      </Form>
    </Modal>
  );
};

export default forwardRef(BmSkipPaymentModal);
